<template>
  <div class="google-register mx-auto">
    <div ref="buttonEl" aria-live="polite"></div>

    <div v-if="errorMessage" class="google-register__error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from "@/stores/user"
import { useI18n } from "vue-i18n"

const userStore = useUserStore()
const router = useRouter()
const { t } = useI18n()
const runtimeConfig = useRuntimeConfig()
const isSubmitting = ref(false)
const errorMessage = ref("")
const toaster = useToaster()

const GOOGLE_SCRIPT_ID = "google-identity-services"
const BACKEND_URL = "/api/verify-google-token"
const buttonEl = ref<HTMLElement | null>(null)

type GoogleCredentialResponse = {
  credential: string
  select_by?: string
}

function loadGoogleScript(): Promise<void> {
  if (document.getElementById(GOOGLE_SCRIPT_ID)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.id = GOOGLE_SCRIPT_ID
    script.src = "https://accounts.google.com/gsi/client"
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("google_script_load_failed"))
    document.head.appendChild(script)
  })
}

async function sendIdTokenToBackend(token: string) {
  errorMessage.value = ""
  isSubmitting.value = true

  const { data } = await useCustomFetch(BACKEND_URL, {
    method: "post",
    body: {
      token,
    },
  }).finally(() => {
    isSubmitting.value = false
  })

  if (!data.value?.success) {
    toaster.show({
      title: t("error"),
      message: data.value.message,
      color: "danger",
      icon: "lucide:alert-triangle",
      closable: true,
    })
    errorMessage.value = data?.value?.message || t("error")
    return
  }

  if (data.value?.success) {
    userStore.setToken(data.value.data.access_token)
    router.push("/home")
  }
}

function handleCredentialResponse(response: GoogleCredentialResponse) {
  if (!response?.credential) {
    errorMessage.value = t("error")
    return
  }

  sendIdTokenToBackend(response.credential)
}

async function initGoogleButton() {
  const clientId =
    "1021880725183-b8kcj52ghngdg16pof27lhq443q4oqus.apps.googleusercontent.com"
  if (!clientId) {
    errorMessage.value = "Google client ID is missing."
    return
  }

  // @ts-expect-error - gsi loaded at runtime
  const google = window.google
  if (!google?.accounts?.id) {
    errorMessage.value = "Google SDK not available."
    return
  }

  google.accounts.id.initialize({
    client_id: clientId,
    callback: handleCredentialResponse,
  })

  const buttonContainer = buttonEl.value
  if (!buttonContainer) {
    return
  }

  // Clear any previous render to support SPA navigation.
  buttonContainer.innerHTML = ""

  google.accounts.id.renderButton(buttonContainer, {
    theme: "outline",
    size: "large",
    width: 320,
    text: "signin_with",
    shape: "pill",
  })
}

onMounted(async () => {
  try {
    await loadGoogleScript()
    await initGoogleButton()
  } catch (error) {
    errorMessage.value = "Google login unavailable."
  }
})

onBeforeUnmount(() => {
  if (buttonEl.value) {
    buttonEl.value.innerHTML = ""
  }
})
</script>

<style lang="scss">
.google-register {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__error {
    color: theme("colors.red.600");
    font-size: 0.75rem;
  }
}
</style>
